import { api, getAccessTokenForFetch } from '../../api'
import type {
  KrakenGraphResponse,
  KrakenChatRequest,
  KrakenChatResponse,
  KrakenStreamEvent,
  KrakenExpandEvent,
  KrakenDiscoverEvent,
  KrakenCreateEntityEvent,
  KrakenEntitySuggestion,
  KrakenEntityUpdateType,
  OctopusConversation,
  OctopusGraph,
  OctopusExport,
  OctopusChatExport,
  ClusterDetailParams,
  ClusterListParams,
  CreateClusterPayload,
  ExpandClusterOptions,
  O2OGraph,
  O2OGraphDetail,
  O2OGraphListResponse,
  UpdateClusterPayload,
} from '../../../types/kraken.types'

export const getNetworkFromVertex = async (
  vertexId: string,
): Promise<KrakenGraphResponse> => {
  const res = await api.get(`/kraken/network/${vertexId}/`)
  const nodes = res.data.nodes ?? []
  const edges = res.data.edges ?? []
  return {
    root: res.data.root ?? nodes[0]?.id,
    nodes,
    edges,
    node_count: res.data.node_count ?? nodes.length,
    edge_count: res.data.edge_count ?? edges.length,
  }
}

/**
 * Chat with Octopus AI (non-streaming fallback)
 * POST /kraken/chat/
 */
export const chatWithKraken = async (data: KrakenChatRequest): Promise<KrakenChatResponse> => {
  const res = await api.post('/kraken/chat/', data)
  return res.data
}

/**
 * Chat with Octopus AI via SSE streaming
 * POST /kraken/chat/stream/
 */
export const chatWithKrakenStream = async (
  data: KrakenChatRequest,
  onEvent: (event: KrakenStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const baseURL = import.meta.env.VITE_API_ENDPOINT
  const token = await getAccessTokenForFetch()

  const response = await fetch(`${baseURL}/kraken/chat/stream/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
    signal,
  })

  if (!response.ok) {
    throw new Error(`Stream request failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    // A chunk that resolved just before abort() landed would otherwise still be
    // parsed and dispatched, letting a dead stream write into the turn that
    // replaced it. Bail before touching the buffer.
    if (signal?.aborted) return

    buffer += decoder.decode(value, { stream: true })

    // Parse SSE lines from buffer
    const lines = buffer.split('\n')
    buffer = lines.pop() || '' // Keep incomplete line in buffer

    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith('data: ')) {
        try {
          const event: KrakenStreamEvent = JSON.parse(trimmed.slice(6))
          onEvent(event)
        } catch {
          // Skip malformed events
        }
      }
      if (signal?.aborted) return
    }
  }

  // Process any remaining buffer
  if (!signal?.aborted && buffer.trim().startsWith('data: ')) {
    try {
      const event: KrakenStreamEvent = JSON.parse(buffer.trim().slice(6))
      onEvent(event)
    } catch {
      // Skip
    }
  }
}

/**
 * Streaming node expansion: Neptune connections + Firecrawl web discovery
 * GET /kraken/expand/<vertex_id>/
 */
export const expandNodeStream = async (
  vertexId: string,
  onEvent: (event: KrakenExpandEvent) => void,
): Promise<void> => {
  const baseURL = import.meta.env.VITE_API_ENDPOINT
  const token = await getAccessTokenForFetch()

  const response = await fetch(`${baseURL}/kraken/expand/${vertexId}/`, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (!response.ok) {
    throw new Error(`Expand request failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith('data: ')) {
        try {
          const event: KrakenExpandEvent = JSON.parse(trimmed.slice(6))
          onEvent(event)
        } catch {
          // Skip malformed events
        }
      }
    }
  }

  if (buffer.trim().startsWith('data: ')) {
    try {
      const event: KrakenExpandEvent = JSON.parse(buffer.trim().slice(6))
      onEvent(event)
    } catch {
      // Skip
    }
  }
}

/**
 * Smart web-presence discovery
 * GET /kraken/discover-web/<vertex_id>/
 */
export const discoverWebPresenceStream = async (
  vertexId: string,
  opts:
    | {
        url?: string
        name?: string
        layers?: number
        categories?: string[]
        conversationId?: string
      }
    | ((event: KrakenDiscoverEvent) => void),
  maybeOnEvent?: (event: KrakenDiscoverEvent) => void,
): Promise<void> => {
  // Back-compat shim: callers used to pass (vertexId, onEvent). Now we also
  // accept (vertexId, { url, name, layers }, onEvent) so the frontend can
  // supply fallback context + how many investigation layers to run.
  const onEvent = typeof opts === 'function' ? opts : maybeOnEvent
  const fallback = typeof opts === 'function' ? {} : opts
  if (!onEvent) throw new Error('discoverWebPresenceStream: missing onEvent callback')

  const baseURL = import.meta.env.VITE_API_ENDPOINT
  const token = await getAccessTokenForFetch()

  const qs = new URLSearchParams()
  if (fallback.url) qs.set('url', fallback.url)
  if (fallback.name) qs.set('name', fallback.name)
  if (fallback.layers) qs.set('layers', String(fallback.layers))
  if (fallback.categories?.length) qs.set('categories', fallback.categories.join(','))
  if (fallback.conversationId) qs.set('conversation_id', fallback.conversationId)
  const query = qs.toString()
  const url = `${baseURL}/kraken/discover-web/${vertexId}/${query ? `?${query}` : ''}`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (!response.ok) {
    throw new Error(`Discover request failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith('data: ')) {
        try {
          const event: KrakenDiscoverEvent = JSON.parse(trimmed.slice(6))
          onEvent(event)
        } catch {
          // Skip malformed events
        }
      }
    }
  }

  if (buffer.trim().startsWith('data: ')) {
    try {
      const event: KrakenDiscoverEvent = JSON.parse(buffer.trim().slice(6))
      onEvent(event)
    } catch {
      // Skip
    }
  }
}

// ─── Create Entity with AI (direct, no chat) ─────────────────────────────────
// Streams the headless "Create entity with AI" flow for a discovered LINK node:
// scrape → reason type → create CustomEntity/CustomProduct. Mirrors
// discoverWebPresenceStream's SSE parsing; the caller drives a node spinner + a
// success/error toast.
export interface CreateEntityFromNodeOpts {
  url?: string
  name?: string
  /** Post/screenshot image url — preserved on a product created from a social post. */
  image?: string
  /** Social platform (instagram/tiktok/facebook) for a social-post node. */
  platform?: string
  /** Post caption — feeds classification + is stored on the product. */
  caption?: string
  /** Poster handle (without '@') so the record isn't named "@unknown". */
  author?: string
  /** e.g. 'social post' — biases the backend to create a product. */
  source_kind?: string
  /** Node the new record should link to (the clicked node's parent). When set,
   *  the backend replaces the clicked node so there is no duplicate. */
  anchor_id?: string
}

export const createEntityFromNodeStream = async (
  vertexId: string,
  opts: CreateEntityFromNodeOpts | ((event: KrakenCreateEntityEvent) => void),
  maybeOnEvent?: (event: KrakenCreateEntityEvent) => void,
): Promise<void> => {
  const onEvent = typeof opts === 'function' ? opts : maybeOnEvent
  const fallback = typeof opts === 'function' ? {} : opts
  if (!onEvent) throw new Error('createEntityFromNodeStream: missing onEvent callback')

  const baseURL = import.meta.env.VITE_API_ENDPOINT
  const token = await getAccessTokenForFetch()

  // POST with the url/name in the body — a discovered link's URL can be very
  // long (Amazon ad-redirect URLs reach ~2 KB), which overflows the request-line
  // limit and gets 403/414-rejected by the edge proxy if sent as a query param.
  const url = `${baseURL}/kraken/create-entity/${vertexId}/`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      ...(fallback.url ? { url: fallback.url } : {}),
      ...(fallback.name ? { name: fallback.name } : {}),
      ...(fallback.image ? { image: fallback.image } : {}),
      ...(fallback.platform ? { platform: fallback.platform } : {}),
      ...(fallback.caption ? { caption: fallback.caption } : {}),
      ...(fallback.author ? { author: fallback.author } : {}),
      ...(fallback.source_kind ? { source_kind: fallback.source_kind } : {}),
      ...(fallback.anchor_id ? { anchor_id: fallback.anchor_id } : {}),
    }),
  })

  if (!response.ok) {
    throw new Error(`Create entity request failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let buffer = ''

  const emit = (raw: string) => {
    try {
      onEvent(JSON.parse(raw.slice(6)) as KrakenCreateEntityEvent)
    } catch {
      // Skip malformed events
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''
    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith('data: ')) emit(trimmed)
    }
  }
  if (buffer.trim().startsWith('data: ')) emit(buffer.trim())
}

// ─── Entity Suggestion Apply ──────────────────────────────────────────────────

// REST endpoint (relative to the API base) that owns each updatable entity kind.
const APPLY_ENDPOINT: Record<KrakenEntityUpdateType, (id: string) => string> = {
  seller: (id) => `sellers/${id}/`,
  saw: (id) => `saw/${id}/`,
  item: (id) => `items/${id}/`,
  entity: (id) => `kraken/entities/${id}/`,
  product: (id) => `kraken/products/${id}/`,
  seizure: (id) => `kraken/seizures/${id}/`,
}

export const applyEntitySuggestions = async (
  entityType: KrakenEntityUpdateType,
  entityId: string,
  suggestions: KrakenEntitySuggestion[],
  appendField = 'description',
): Promise<unknown> => {
  const endpoint = APPLY_ENDPOINT[entityType](entityId)
  const payload: Record<string, unknown> = { id: entityId }
  const appendLines: string[] = []

  for (const s of suggestions) {
    if (s.mode === 'append') {
      // Identifiers without a dedicated column (KVK, social handles, misc facts)
      // are appended to the kind's free-text column rather than overwriting a field.
      const valueText = Array.isArray(s.value) ? s.value.join(', ') : String(s.value)
      const line = s.append_text || `${s.field}: ${valueText}`
      if (line.trim()) appendLines.push(line.trim())
    } else {
      payload[s.field] = s.value
    }
  }

  if (appendLines.length) {
    // Read the current free-text column so we extend it instead of clobbering it.
    let existing = ''
    try {
      const current = await api.get(endpoint)
      existing = ((current.data as Record<string, unknown> | undefined)?.[appendField] as string) || ''
    } catch {
      // If the read fails, still apply the new lines on their own.
    }
    const block = appendLines.join('\n')
    payload[appendField] = existing.trim() ? `${existing.trimEnd()}\n${block}` : block
  }

  const res = await api.patch(endpoint, payload)
  return res.data
}

// ─── Conversation Persistence ────────────────────────────────────────────────

export const createConversation = async (data: { name?: string }): Promise<OctopusConversation> => {
  const res = await api.post<OctopusConversation>('/kraken/conversations/', data)
  return res.data
}

export const getConversation = async (id: string, expand?: string, otpToken?: string): Promise<OctopusConversation> => {
  const params: Record<string, string> = {}
  if (expand) params.expand = expand
  if (otpToken) params.otp_token = otpToken
  const res = await api.get<OctopusConversation>(`/kraken/conversations/${id}/`, { params })
  return res.data
}

export const patchConversation = async (id: string, data: Partial<OctopusConversation>): Promise<OctopusConversation> => {
  const res = await api.patch<OctopusConversation>(`/kraken/conversations/${id}/`, data)
  return res.data
}

export const listConversations = async (): Promise<OctopusConversation[]> => {
  const res = await api.get<OctopusConversation[]>('/kraken/conversations/')
  return res.data
}

export const deleteConversation = async (id: string): Promise<void> => {
  await api.delete(`/kraken/conversations/${id}/`)
}

// ─── OctopusGraph Persistence (no messages) ──────────────────────────────────

export const createGraph = async (data: { name?: string; network_context?: Record<string, unknown>; favorite?: boolean }): Promise<OctopusGraph> => {
  const res = await api.post<OctopusGraph>('/kraken/graphs/', data)
  return res.data
}

export const getGraph = async (id: string, otp_token?: string): Promise<OctopusGraph> => {
  const res = await api.get<OctopusGraph>(`/kraken/graphs/${id}/`, {
    params: { otp_token },
  })
  return res.data
}

export const patchGraph = async (id: string, data: Partial<OctopusGraph>): Promise<OctopusGraph> => {
  const res = await api.patch<OctopusGraph>(`/kraken/graphs/${id}/`, data)
  return res.data
}

export const listGraphs = async (): Promise<OctopusGraph[]> => {
  const res = await api.get<OctopusGraph[]>('/kraken/graphs/')
  return res.data
}

export const deleteGraph = async (id: string): Promise<void> => {
  await api.delete(`/kraken/graphs/${id}/`)
}

export const createExport = async (
  graphId: string,
  filters?: { edgeTypes?: string[]; minWeight?: number },
): Promise<OctopusExport> => {
  const filterPayload: Record<string, unknown> = {}
  if (filters?.edgeTypes?.length) filterPayload.edge_types = filters.edgeTypes.join(',')
  if (filters?.minWeight) filterPayload.min_weight = String(filters.minWeight)
  const res = await api.post<OctopusExport>('/kraken/exports/', { graph: graphId, filters: filterPayload })
  return res.data
}

export const listExports = async (graphId: string): Promise<OctopusExport[]> => {
  const res = await api.get('/kraken/exports/', { params: { graph: graphId } })
  const data = res.data
  return Array.isArray(data) ? data : data?.data ?? []
}

export const deleteExport = async (id: string): Promise<void> => {
  await api.delete(`/kraken/exports/${id}/`)
}

// ─── Chat (Conversation) PDF Exports ──────────────────────────────────────────

export const createChatExport = async (conversationId: string): Promise<OctopusChatExport> => {
  const res = await api.post<OctopusChatExport>('/kraken/chat-exports/', { conversation: conversationId })
  return res.data
}

export const listChatExports = async (conversationId: string): Promise<OctopusChatExport[]> => {
  const res = await api.get('/kraken/chat-exports/', { params: { conversation: conversationId } })
  const data = res.data
  return Array.isArray(data) ? data : data?.data ?? []
}

export const deleteChatExport = async (id: string): Promise<void> => {
  await api.delete(`/kraken/chat-exports/${id}/`)
}

// ─── O2O Graphs (KrakenO2O) ───────────────────────────────────────────────────

export const getClusterList = async (params?: ClusterListParams): Promise<O2OGraphListResponse> => {
  const res = await api.get<O2OGraphListResponse>('/o2o/graphs/', { params })
  return res.data
}

export const getClusterDetail = async (id: string, params?: ClusterDetailParams): Promise<O2OGraphDetail> => {
  const res = await api.get<O2OGraphDetail>(`/o2o/graphs/${id}/`, { params })
  return res.data
}

export const postCluster = async (data: CreateClusterPayload): Promise<O2OGraph> => {
  const res = await api.post<O2OGraph>('/o2o/graphs/', data)
  return res.data
}

export const patchCluster = async (id: string, data: UpdateClusterPayload): Promise<O2OGraph> => {
  const res = await api.patch<O2OGraph>(`/o2o/graphs/${id}/`, data)
  return res.data
}

export const deleteCluster = async (id: string): Promise<void> => {
  await api.delete(`/o2o/graphs/${id}/`)
}

export const expandCluster = async (id: string, options?: ExpandClusterOptions): Promise<void> => {
  const params = options?.expand ? { expand: options.expand } : {}
  const body = {
    ...(options?.expand_all && { expand_all: options.expand_all }),
    ...(options?.node_id && { node_id: options.node_id }),
  }
  await api.post(`/o2o/graphs/${id}/expand/`, body, { params })
}

export const getClusterExpansionStatus = async (id: string, params?: ClusterDetailParams): Promise<O2OGraphDetail> => {
  const res = await api.get<O2OGraphDetail>(`/o2o/graphs/${id}/expand/`, { params })
  return res.data
}

export const expandNode = async (id: string, nodeId: string): Promise<void> => {
  await api.post(`/o2o/graphs/${id}/expand_node/`, { node_id: nodeId })
}
